import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BiArrowBack } from 'react-icons/bi';

const BarrelBlanketAllEntries = () => {
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchTimeout, setSearchTimeout] = useState(null);

    useEffect(() => {
        fetchAllEntries();
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const fetchAllEntries = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("apUserToken");

            if (!token) {
                alert('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}barrelBlanket/list?page=1`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setEntries(response.data.data.data || []);
            } else {
                alert(response.data.message || 'Failed to fetch entries');
            }
        } catch (error) {
            console.error('API Error:', error);
            alert('Failed to fetch entries. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await fetchAllEntries();
        setRefreshing(false);
    };

    const getSearchTerm = (text) => {
        setSearchTerm(text);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if (!text || text.trim() === '') {
            setSearchResults([]);
            return;
        }

        const timeout = setTimeout(() => {
            searchData(text);
        }, 500);

        setSearchTimeout(timeout);
    };

    const searchData = async (data) => {
        try {
            const token = localStorage.getItem("apUserToken");

            if (!token) {
                alert('Authentication token not found. Please login again.');
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}barrelBlanket/search?query=${data}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setSearchResults(response.data.data || []);
            } else {
                alert(response.data.message || 'Failed to search entries');
            }
        } catch (error) {
            console.error('Search API Error:', error);
            alert('Failed to search entries. Please try again.');
        }
    };

    const displayEntries = searchTerm.length > 0 ? searchResults : entries;

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-teal-600 text-white p-4 shadow-md">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/barrel-blanket')} className="text-white hover:text-gray-200">
                        <BiArrowBack size={24} />
                    </button>
                    <h1 className="text-2xl font-bold">All Barrel Blanket Entries</h1>
                </div>
            </div>

            {/* Search */}
            <div className="container mx-auto p-4 max-w-4xl">
                <input
                    type="text"
                    className="w-full h-12 border border-gray-300 rounded-lg px-4 mb-4"
                    placeholder="Search Entries"
                    value={searchTerm}
                    onChange={(e) => getSearchTerm(e.target.value)}
                />

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading entries...</p>
                        </div>
                    </div>
                ) : displayEntries.length > 0 ? (
                    <div className="space-y-4">
                        {displayEntries.map((item, index) => (
                            <div
                                key={item._id || index}
                                onClick={() => navigate(`/barrel-blanket/details/${item._id}`)}
                                className="bg-gray-100 p-5 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
                            >
                                <h3 className="text-lg font-bold text-teal-600 mb-2">
                                    {item.companyName || 'N/A'}, {item.city || 'N/A'}, {item.state || 'N/A'}
                                </h3>
                                <p className="text-sm text-teal-600">User: {item.username || 'N/A'}</p>
                                <p className="text-sm text-teal-600">Primary Customer Contact: {item.utility || 'N/A'}</p>
                                <p className="text-xs text-teal-600 mt-2">
                                    Date Time of Record: {new Date(item.createdAt).toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-64">
                        <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-lg font-bold text-gray-600">
                            {searchTerm.length > 0 ? 'No search results found' : 'No entries found'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            {searchTerm.length > 0 
                                ? 'Try searching with different keywords.'
                                : 'No barrel blanket investigation forms have been submitted yet.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BarrelBlanketAllEntries;

